/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OVERLAY_ANIMATION_DURATION } from '../../config';
import { DeviceModel } from '../../core/device';
import { CursorManager } from '../../core/navigation';
import { RootState } from '../../store/root.reducers';
import { setOverlayHighlightVerticalLimitAction } from '../../store/overlay/overlayhighlight/overlayhighlight.actions';
import { Mousetrap } from '../common';
import {
    overlayDisableGhostMode,
    overlayEnableGhostMode,
} from '../../store/overlay/overlayhighlight/overlayhighlight.reducers';
import { overlayDialogSetCaretVisible } from '../../store/overlaydialog/overlaydialog.reducers';
import { TextView, BrandingIcon, Seperator, SelectOption, Button } from './common';
import SelectMarkButton from './common/Body/SelectMarkButton/SelectMarkButton';
import { TextViewClasses } from './common/TextView/TextViewClasses';
import { OverlayInteractiveLayer } from './OverlayInteractiveLayer';
import {
    DescriptionType,
    IDescriptionEntry,
    IOverlayDialog,
    IOverlayOptions,
} from '../../store/overlaydialog/overlaydialogprops';
import { SeparatorClasses } from './common/Body/Seperator/SeparatorClasses';

import './OverlayDialog.scss';

const OverlayDialog: FunctionComponent = (): JSX.Element => {
    const dispatch = useDispatch();
    const { verticalPosition } = useSelector((state: RootState) => state.overlayHighlight);
    const {
        uiProperties: uiProps,
        isOverlayDialogActive,
    }: { uiProperties: IOverlayDialog; isOverlayDialogActive: boolean } = useSelector(
        (state: RootState) => state.overlayDialog,
    );
    const showOverlay = useRef(false);

    useEffect(() => {
        if (uiProps.options?.length) {
            dispatch(setOverlayHighlightVerticalLimitAction(uiProps.options.length));
        }
        return () => {
            dispatch(setOverlayHighlightVerticalLimitAction(0));
        };
    }, [uiProps.options]);

    const onCursorStateChangeCallback = (state) => {
        if (!state) dispatch(overlayDisableGhostMode());
        else dispatch(overlayEnableGhostMode());
    };

    useEffect(() => {
        const ref = CursorManager.subscribe(onCursorStateChangeCallback);
        return () => {
            CursorManager.unsubscribe(ref);
        };
    }, []);

    useEffect(() => {
        let timerId;
        if (!isOverlayDialogActive) {
            timerId = setTimeout(() => {
                showOverlay.current = false;
            }, OVERLAY_ANIMATION_DURATION);
        } else {
            showOverlay.current = true;
            clearTimeout(timerId);
        }
        return () => {
            clearTimeout(timerId);
        };
    }, [isOverlayDialogActive]);

    useEffect(() => {
        if (
            uiProps.enableCaret &&
            isOverlayDialogActive &&
            uiProps.verticalPositionCaretAvailable?.indexOf(verticalPosition) !== -1
        )
            dispatch(overlayDialogSetCaretVisible());
    }, [CursorManager.isCursorAppeared()]);

    // subTitile division.
    const subtitileDiv = uiProps.subtitle ? (
        <TextView
            type={TextViewClasses.SUBTITLE}
            text={typeof uiProps.subtitle !== 'string' ? uiProps.subtitle.text : uiProps.subtitle}
            iconUrl={typeof uiProps.subtitle !== 'string' ? uiProps.subtitle.icon : ''}
        />
    ) : null;

    // brandingicon division.
    const brandingIconDiv = <BrandingIcon iconURL={uiProps.iconURL} />;

    // messageCode division.
    const messageCodeDiv = uiProps.messageCode ? (
        <TextView type={TextViewClasses.MESSAGE_CODE} text={uiProps.messageCode} />
    ) : null;

    const decorateDescription = (description: string | Array<IDescriptionEntry>) => {
        if (typeof description === 'string')
            return (
                <TextView type={TextViewClasses.DESCRIPTION} text={uiProps.description as string} />
            );
        return description.map((descriptionEntry) => {
            const descriptionEntryDiv: Array<JSX.Element> = [];
            if (descriptionEntry.hrTop)
                descriptionEntryDiv.push(<Seperator type={SeparatorClasses.DESCRIPTION} />);
            descriptionEntryDiv.push(
                <TextView
                    type={
                        descriptionEntry.descriptionType === DescriptionType.PROGRAM_DESCRIPTION
                            ? TextViewClasses.PROGRAM_DESCRIPTION
                            : TextViewClasses.DESCRIPTION
                    }
                    text={descriptionEntry.text}
                />,
            );
            if (descriptionEntry.hrBottom)
                descriptionEntryDiv.push(<Seperator type={SeparatorClasses.DESCRIPTION} />);
            return descriptionEntryDiv;
        });
    };

    // description division.
    const descriptionDiv = uiProps.description ? decorateDescription(uiProps.description) : null;

    const selectOptionsDiv = (index, option) => {
        return (
            <div key={index}>
                <Seperator />
                {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                    <OverlayInteractiveLayer />
                ) : null}
                <SelectOption
                    options={option.horizontalSelectionoptions}
                    isFocused={index === verticalPosition}
                    text={`${option.text}:`}
                    id={index}
                />
            </div>
        );
    };

    const selectMarkDiv = (index, option: IOverlayOptions) => {
        let button;

        if (option.hasSelectMark) {
            button = (
                <SelectMarkButton
                    text={option.text}
                    isFocused={index === verticalPosition}
                    isSelected={option.isSelected}
                    id={index}
                />
            );
        } else {
            button = (
                <Button
                    text={option.text}
                    isFocused={index === verticalPosition}
                    id={index}
                    displayValue={option.displayValue}
                />
            );
        }

        return (
            <Mousetrap value={index} key={index}>
                {button}
            </Mousetrap>
        );
    };

    // buttons division.
    const buttonsDiv: Array<JSX.Element> = [];
    if (uiProps.options)
        uiProps.options.forEach((option, index) => {
            if (option.hrTop) buttonsDiv.push(<Seperator type={SeparatorClasses.OPTIONS} />);
            if (option.horizontalSelectionoptions) buttonsDiv.push(selectOptionsDiv(index, option));
            else buttonsDiv.push(selectMarkDiv(index, option));
            if (option.hrBottom) buttonsDiv.push(<Seperator type={SeparatorClasses.OPTIONS} />);
        });

    // dimmer div to be used behind overlays other than shown on playback screen.
    const dimmerClass = uiProps.hasDimmer ? 'dimmer' : 'no-dimmer';
    const dimmerDiv = isOverlayDialogActive ? (
        <Mousetrap className={dimmerClass} value={uiProps.mouseTrapValue}>
            <div className={dimmerClass} />
        </Mousetrap>
    ) : null;

    // seperator div
    const seperatorDiv = uiProps.hasSeperator ? <Seperator /> : null;

    return (
        <div
            className={`overlay-dialog-wrapper ${
                !showOverlay.current ? 'overlay-dialog-hidden' : ''
            }`}
        >
            {dimmerDiv}
            <div
                className={`overlay-dialog-container ${
                    isOverlayDialogActive ? 'show-dialog ' : 'hide-dialog'
                }`}
                data-testid="overlay-dialog-container"
            >
                <div className="overlay-header">
                    <div className="row-data">
                        <TextView type={TextViewClasses.TITLE} text={uiProps.title} />
                        {messageCodeDiv}
                    </div>
                    <div className="row-data" data-testid="overlay-title">
                        {subtitileDiv}
                        {brandingIconDiv}
                    </div>
                </div>
                <div className="overlay-body">
                    {descriptionDiv}
                    {seperatorDiv}
                    {buttonsDiv}
                </div>
            </div>
        </div>
    );
};

export default OverlayDialog;
